import { Container } from "layouts";
import PaddingX from "layouts/PaddingX";
import { useState } from "react";
import { Calendar } from "primereact/calendar";
import { BiCalendar } from "react-icons/bi";
import { BsFileEarmarkExcel } from "react-icons/bs";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { ProgressBar } from "primereact/progressbar";
import { FiBarChart2, FiDownload, FiRefreshCw, FiUsers, FiBookOpen } from "react-icons/fi";
import moment from "moment";
import useGenerateHlfReport from "api/HlfReportResource/useGenerateHlfReport";
import useGenerateUserLoginReport from "api/HlfReportResource/useGenerateUserLoginReport";

const HlfReport = () => {
	const [fromDate, setFromDate] = useState("");
	const [toDate, setToDate] = useState("");
	const [progress, setProgress] = useState(0);
	const [downloadUrl, setDownloadUrl] = useState("");
	const [activeTab, setActiveTab] = useState("students"); // "students" or "logins"

	const { mutateAsync: generateStudentsReport, isLoading: isGeneratingStudents } =
		useGenerateHlfReport();

	const { mutateAsync: generateLoginReport, isLoading: isGeneratingLogins } =
		useGenerateUserLoginReport();

	const handleGenerateReport = async () => {
		if (!fromDate || !toDate) {
			return;
		}

		if (moment(fromDate).isAfter(moment(toDate))) {
			return;
		}

		setProgress(0);
		setDownloadUrl("");

		try {
			// Simulate progress updates
			const progressInterval = setInterval(() => {
				setProgress((prev) => {
					if (prev >= 90) {
						clearInterval(progressInterval);
						return 90;
					}
					return prev + 10;
				});
			}, 500);

			const response =
				activeTab === "students"
					? await generateStudentsReport({
							from_date: moment(fromDate).format("YYYY-MM-DD"),
							to_date: moment(toDate).format("YYYY-MM-DD"),
					  })
					: await generateLoginReport({
							from_date: moment(fromDate).format("YYYY-MM-DD"),
							to_date: moment(toDate).format("YYYY-MM-DD"),
					  });

			clearInterval(progressInterval);
			setProgress(100);

			// Wait a moment to show 100% completion
			setTimeout(() => {
				setProgress(0);

				if (response?.data?.download_url) {
					setDownloadUrl(response.data.download_url);
				}
			}, 1000);
		} catch (error) {
			setProgress(0);
			// Error handling is done in the API hook
		}
	};

	const handleDownload = () => {
		if (downloadUrl) {
			window.open(downloadUrl, "_blank");
		}
	};

	const isFormValid = fromDate && toDate && !moment(fromDate).isAfter(moment(toDate));

	const isGenerating = isGeneratingStudents || isGeneratingLogins;

	return (
		<Container>
			<PaddingX>
				<div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-4 sm:py-6 lg:py-12">
					<div className="max-w-6xl mx-auto px-2 sm:px-4 lg:px-8">
						{/* Header with enhanced styling */}
						<div className="text-center mb-8 sm:mb-10 lg:mb-12">
							<div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-r from-akgreenest to-akgreener rounded-xl sm:rounded-2xl mb-4 sm:mb-6 shadow-lg">
								<FiBarChart2 className="text-white text-lg sm:text-xl lg:text-2xl" />
							</div>
							<h1 className="text-2xl md:text-3xl font-bold mb-3 sm:mb-4 px-4">
								HLF Report Generator
							</h1>
							<p className="text-sm sm:text-base text-gray-600 max-w-xl mx-auto leading-relaxed px-4">
								Generate comprehensive reports within your selected date range
							</p>
						</div>

						{/* Main Layout with Vertical Tabs */}
						<div className="flex flex-col md:flex-row gap-6 lg:gap-8">
							{/* Vertical Tabs Sidebar */}
							<div className="w-full md:w-80 flex-shrink-0">
								<div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg border">
									<h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 sm:mb-6">
										Report Type
									</h3>
									<div className="space-y-2 ">
										<button
											onClick={() => {
												setActiveTab("students");
												setDownloadUrl("");
											}}
											className={`w-full flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3 sm:py-4 rounded-lg sm:rounded-xl font-semibold transition-all duration-300 text-left min-h-[60px] sm:min-h-[70px] ${
												activeTab === "students"
													? "bg-gradient-to-r from-akgreener to-akgreen text-white shadow-lg"
													: "text-gray-600 hover:text-gray-800 hover:bg-gray-50 active:bg-gray-100"
											}`}
										>
											<FiBookOpen size={18} className="flex-shrink-0 sm:w-5 sm:h-5" />
											<div className="min-w-0 flex-1">
												<div className="font-semibold text-sm leading-tight">
													Students Assigned Books
												</div>
												<div className="text-xs opacity-80 mt-1">Track book assignments</div>
											</div>
										</button>
										<button
											onClick={() => {
												setActiveTab("logins");
												setDownloadUrl("");
											}}
											className={`w-full flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3 sm:py-4 rounded-lg sm:rounded-xl font-semibold transition-all duration-300 text-left min-h-[60px] sm:min-h-[70px] ${
												activeTab === "logins"
													? "bg-gradient-to-r from-akgreener to-akgreen text-white shadow-lg"
													: "text-gray-600 hover:text-gray-800 hover:bg-gray-50 active:bg-gray-100"
											}`}
										>
											<FiUsers size={18} className="flex-shrink-0 sm:w-5 sm:h-5" />
											<div className="min-w-0 flex-1">
												<div className="font-semibold text-sm  leading-tight">
													Students Login Activity
												</div>
												<div className="text-xs opacity-80 mt-1">Monitor user access</div>
											</div>
										</button>
									</div>
								</div>
							</div>

							{/* Main Content Area */}
							<div className="flex-1">
								<div className="backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-xl border bg-white">
									{/* Date Range Selection with improved layout */}
									<div className="mb-8 sm:mb-10">
										<div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
											<div className="w-1 h-6 sm:h-8 bg-gradient-to-b from-akgreener to-akgreen rounded-full"></div>
											<h2 className="text-lg sm:text-xl l font-semibold">Select date range</h2>
										</div>

										<div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
											<div className="space-y-2 sm:space-y-3">
												<label className="block text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wide">
													Start Date
												</label>
												<div className="relative group">
													<span className="p-float-label">
														<Calendar
															id="fromDate"
															className="font-sans w-full"
															value={fromDate}
															onChange={(e) => setFromDate(e.target.value)}
															dateFormat="yy-mm-dd"
															mask="9999-99-99"
															panelClassName="font-sans text-sm shadow-2xl border-0 rounded-xl z-50"
															showIcon
															icon={<BiCalendar size={18} className="text-white" />}
															inputClassName="rounded-lg sm:rounded-xl border-2 border-gray-200 bg-white/90 backdrop-blur-sm pl-3 sm:pl-4 pr-10 sm:pr-12 text-sm h-12 sm:h-14 transition-all duration-300 focus:border-blue-500 focus:ring-2 sm:focus:ring-4 focus:ring-blue-100 hover:border-gray-300 touch-manipulation"
															placeholder="Select start date"
															maxDate={toDate || new Date()}
															touchUI={true}
														/>
													</span>
												</div>
											</div>

											<div className="space-y-2 sm:space-y-3">
												<label className="block text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wide">
													End Date
												</label>
												<div className="relative group">
													<span className="p-float-label">
														<Calendar
															id="toDate"
															className="font-sans w-full"
															value={toDate}
															onChange={(e) => setToDate(e.target.value)}
															dateFormat="yy-mm-dd"
															mask="9999-99-99"
															panelClassName="font-sans text-sm shadow-2xl border-0 rounded-xl z-50"
															showIcon
															icon={<BiCalendar size={18} className="text-white" />}
															inputClassName="rounded-lg sm:rounded-xl border-2 border-gray-200 bg-white/90 backdrop-blur-sm pl-3 sm:pl-4 pr-10 sm:pr-12 text-sm h-12 sm:h-14 transition-all duration-300 focus:border-blue-500 focus:ring-2 sm:focus:ring-4 focus:ring-blue-100 hover:border-gray-300 touch-manipulation"
															placeholder="Select end date"
															minDate={fromDate}
															maxDate={new Date()}
															touchUI={true}
														/>
													</span>
												</div>
											</div>
										</div>
									</div>

									{/* Progress Bar with enhanced styling */}
									{isGenerating && (
										<div className="mb-6 sm:mb-8 p-4 sm:p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl sm:rounded-2xl border border-blue-100">
											<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4">
												<div className="flex items-center gap-2 sm:gap-3">
													<div className="w-2 h-2 sm:w-3 sm:h-3 bg-blue-500 rounded-full animate-pulse"></div>
													<span className="text-xs sm:text-sm font-semibold text-gray-700">
														Generating{" "}
														{activeTab === "students"
															? "Students Assigned Books"
															: "User Login Activity"}{" "}
														Report...
													</span>
												</div>
												<span className="text-xs sm:text-sm font-bold text-blue-600 bg-blue-100 px-2 sm:px-3 py-1 rounded-full self-start sm:self-auto">
													{progress}%
												</span>
											</div>
											<ProgressBar
												value={progress}
												className="h-2 sm:h-3 rounded-full overflow-hidden"
												color="#3B82F6"
											/>
											<p className="text-xs text-gray-600 mt-3 flex items-start gap-2">
												<AiOutlineLoading3Quarters className="animate-spin flex-shrink-0 mt-0.5" />
												<span className="leading-relaxed">
													Please wait while we compile your{" "}
													{activeTab === "students"
														? "students assigned books"
														: "user login activity"}{" "}
													report. This may take a few moments.
												</span>
											</p>
										</div>
									)}

									{/* Action Buttons with enhanced styling */}
									<div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
										{!isGenerating && !downloadUrl && (
											<button
												onClick={handleGenerateReport}
												disabled={!isFormValid}
												className={`flex-1 flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 lg:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-semibold transition-all duration-300 transform hover:scale-105 active:scale-95 min-h-[48px] sm:min-h-[56px] ${
													isFormValid
														? "bg-gradient-to-r from-akgreener to-akgreen text-white shadow-lg hover:shadow-xl"
														: "bg-gray-100 text-gray-400 cursor-not-allowed"
												}`}
											>
												<BsFileEarmarkExcel size={18} className="sm:w-5 sm:h-5" />
												<span className="text-sm ">Generate</span>
											</button>
										)}

										{isGenerating && (
											<button
												disabled
												className="flex-1 flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 lg:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-semibold bg-gradient-to-r from-blue-500 to-indigo-500 text-white cursor-not-allowed shadow-lg min-h-[48px] sm:min-h-[56px]"
											>
												<AiOutlineLoading3Quarters
													size={18}
													className="animate-spin sm:w-5 sm:h-5"
												/>
												<span className="text-sm">Generating...</span>
											</button>
										)}

										{downloadUrl && !isGenerating && (
											<button
												onClick={handleDownload}
												className="flex-1 flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 lg:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-semibold bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 transition-all duration-300 min-h-[48px] sm:min-h-[56px]"
											>
												<FiDownload size={18} className="sm:w-5 sm:h-5" />
												<span className="text-sm ">Download Excel Report</span>
											</button>
										)}

										{/* Reset Button with enhanced styling */}
										{!isGenerating && (
											<button
												onClick={() => {
													setFromDate("");
													setToDate("");
													setDownloadUrl("");
												}}
												className="px-4 sm:px-6 lg:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-semibold border-2 border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 active:bg-gray-100 transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 min-h-[48px] sm:min-h-[56px]"
											>
												<FiRefreshCw size={16} className="sm:w-4 sm:h-4" />
												<span className="text-sm ">Reset</span>
											</button>
										)}
									</div>

									{/* Success message when download is ready */}
									{downloadUrl && !isGenerating && (
										<div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl sm:rounded-2xl border border-green-200">
											<div className="flex items-start gap-2 sm:gap-3">
												<div className="w-2 h-2 bg-green-500 rounded-full animate-pulse flex-shrink-0 mt-1.5"></div>
												<p className="text-xs sm:text-sm font-medium text-green-700 leading-relaxed">
													{activeTab === "students"
														? "Students Assigned Books"
														: "Students Login Activity"}{" "}
													report generated successfully! Click the download button above to get your
													Excel file.
												</p>
											</div>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</PaddingX>
		</Container>
	);
};

export default HlfReport;
